// Admin > Legal & Policies: the Grievance Officer and jurisdiction details the documents use,
// and the documents themselves (Terms, Privacy Policy, Vendor Agreement, Refunds, Legal Notices,
// contract key points). Each edit is published as a new version; documents that require
// acceptance are then accepted again by users.
import React, { useEffect, useState } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box, Typography, Paper, Grid, TextField, Button, Alert, Stack, Chip, Table, TableHead, TableRow, TableCell, TableBody,
  Dialog, DialogTitle, DialogContent, DialogActions, Switch, FormControlLabel, MenuItem, ToggleButtonGroup, ToggleButton, Link,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import EditIcon from '@mui/icons-material/Edit';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import {
  LEGAL_SETTINGS_QUERY, SAVE_LEGAL_SETTINGS_MUTATION, LEGAL_DOCUMENTS_QUERY, LEGAL_DOCUMENT_VERSIONS_QUERY,
  PUBLISH_LEGAL_DOCUMENT_MUTATION, LEGAL_ACCEPTANCE_STATS_QUERY,
} from '../graphql/queries';
import MarkdownView from '../components/MarkdownView';

const FIELDS = [
  ['grievanceOfficerName', 'Grievance Officer name', 'Required by the IT Rules, 2021 and the E-Commerce Rules, 2020'],
  ['grievanceOfficerDesignation', 'Designation', ''],
  ['grievanceOfficerEmail', 'Grievance Officer email', 'Defaults to the company email'],
  ['grievanceOfficerPhone', 'Grievance Officer phone', 'Defaults to the company phone'],
  ['grievanceOfficerAddress', 'Grievance Officer address', 'Defaults to the company address'],
  ['dataProtectionEmail', 'Data protection (DPDP) requests email', 'Defaults to the Grievance Officer email'],
  ['jurisdictionCity', 'Courts having jurisdiction (city)', 'e.g. Chennai'],
  ['arbitrationSeat', 'Seat of arbitration (city)', 'Defaults to the jurisdiction city'],
];
const SLUG = { terms: 'terms', privacy: 'privacy', vendor_terms: 'vendor-terms', refunds: 'refunds', notices: 'notices' };
const PLACEHOLDERS = '{{companyName}} {{brandName}} {{companyAddress}} {{companyEmail}} {{companyPhone}} {{companyWebsite}} {{companyCin}} {{companyGstin}} {{grievanceOfficerName}} {{grievanceOfficerDesignation}} {{grievanceOfficerEmail}} {{grievanceOfficerPhone}} {{grievanceOfficerAddress}} {{dataProtectionEmail}} {{jurisdictionCity}} {{arbitrationSeat}}';

function EditDialog({ docKey, onClose, onPublished }) {
  const { data } = useQuery(LEGAL_DOCUMENT_VERSIONS_QUERY, { variables: { key: docKey }, fetchPolicy: 'network-only' });
  const versions = data?.legalDocumentVersions || [];
  const [form, setForm] = useState(null);
  const [view, setView] = useState('edit');
  const [error, setError] = useState('');
  useEffect(() => {
    if (versions[0] && !form) {
      const v = versions[0];
      setForm({ title: v.title, summary: v.summary || '', body: v.body, effectiveDate: new Date().toISOString().slice(0, 10), requiresAcceptance: v.requiresAcceptance, audience: v.audience });
    }
  }, [versions, form]);
  const [publish, { loading }] = useMutation(PUBLISH_LEGAL_DOCUMENT_MUTATION, {
    onCompleted: (d) => onPublished(`Published version ${d.publishLegalDocument.version}`),
    onError: (e) => setError(e.message),
  });
  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Edit {versions[0]?.title || ''} <Typography component="span" variant="body2" color="text.secondary">(current: version {versions[0]?.version})</Typography></DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {form && (
          <Stack spacing={2} mt={1}>
            <TextField label="Title" value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} fullWidth />
            <TextField label="Summary (plain language, shown first)" value={form.summary} onChange={e => setForm(f => ({ ...f, summary: e.target.value }))} fullWidth multiline minRows={2} />
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField label="Effective date" type="date" value={form.effectiveDate} onChange={e => setForm(f => ({ ...f, effectiveDate: e.target.value }))} InputLabelProps={{ shrink: true }} />
              <TextField select label="Who it applies to" value={form.audience} onChange={e => setForm(f => ({ ...f, audience: e.target.value }))} sx={{ minWidth: 200 }}>
                <MenuItem value="all">Everyone</MenuItem>
                <MenuItem value="family">Bride / groom families</MenuItem>
                <MenuItem value="vendor">Vendors</MenuItem>
                <MenuItem value="matchmaker">Matchmakers</MenuItem>
              </TextField>
              <FormControlLabel control={<Switch checked={form.requiresAcceptance} onChange={e => setForm(f => ({ ...f, requiresAcceptance: e.target.checked }))} />}
                label="Users must accept it" />
            </Stack>
            <ToggleButtonGroup exclusive size="small" value={view} onChange={(_, v) => v && setView(v)}>
              <ToggleButton value="edit">Edit</ToggleButton>
              <ToggleButton value="preview">Preview</ToggleButton>
            </ToggleButtonGroup>
            {view === 'edit' ? (
              <TextField label="Content (Markdown: ## headings, - lists, 1. numbered, **bold**, [link](/legal/terms))" value={form.body}
                onChange={e => setForm(f => ({ ...f, body: e.target.value }))} fullWidth multiline minRows={16}
                InputProps={{ sx: { fontFamily: 'monospace', fontSize: 13 } }}
                helperText={`Placeholders filled in automatically: ${PLACEHOLDERS}`} />
            ) : (
              <Paper variant="outlined" sx={{ p: 2, maxHeight: 480, overflowY: 'auto' }}><MarkdownView text={form.body} /></Paper>
            )}
            {form.requiresAcceptance && <Alert severity="info">Publishing asks every user it applies to to accept the new version when they next use the app.</Alert>}
            <Alert severity="warning">Have changes reviewed by a lawyer practising in India before publishing.</Alert>
            {versions.length > 1 && (
              <Box>
                <Typography variant="subtitle2" fontWeight={700}>Earlier versions</Typography>
                {versions.slice(1).map(v => (
                  <Typography key={v.version} variant="body2" color="text.secondary">Version {v.version} · effective {v.effectiveDate} · published {new Date(v.publishedAt).toLocaleDateString('en-IN')}</Typography>
                ))}
              </Box>
            )}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" disabled={!form || loading} onClick={() => publish({ variables: { input: { key: docKey, ...form } } })}>
          {loading ? 'Publishing…' : 'Publish new version'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default function AdminLegalPage() {
  const { data: sData, refetch: refetchSettings } = useQuery(LEGAL_SETTINGS_QUERY, { fetchPolicy: 'network-only' });
  const { data: dData, refetch: refetchDocs } = useQuery(LEGAL_DOCUMENTS_QUERY, { fetchPolicy: 'network-only' });
  const { data: stData, refetch: refetchStats } = useQuery(LEGAL_ACCEPTANCE_STATS_QUERY, { fetchPolicy: 'network-only' });
  const [form, setForm] = useState(null);
  const [msg, setMsg] = useState(null);
  const [editing, setEditing] = useState(null);
  const s = sData?.legalSettings;
  useEffect(() => { if (s && !form) setForm(Object.fromEntries(FIELDS.map(([k]) => [k, s[k] || '']))); }, [s, form]);
  const [save, { loading }] = useMutation(SAVE_LEGAL_SETTINGS_MUTATION, {
    onCompleted: () => { setMsg({ severity: 'success', text: 'Legal details saved — the documents now show them.' }); refetchSettings(); refetchDocs(); },
    onError: (e) => setMsg({ severity: 'error', text: e.message }),
  });
  const docs = dData?.legalDocuments || [];
  const stats = stData?.legalAcceptanceStats || [];
  const missing = s?.missing || [];

  return (
    <Box>
      <Typography variant="h4" fontWeight={700}>Legal & Policies</Typography>
      <Typography variant="body2" color="text.secondary" mb={2}>
        The Terms and Conditions, Privacy Policy and other documents shown on the website and accepted by users. They are filled in
        with the details in <Link component={RouterLink} to="/dashboard/admin/company">Company Details</Link> and below.
      </Typography>
      <Alert severity="warning" sx={{ mb: 2 }}>
        These documents are written for Indian law (IT Act 2000 and IT Rules 2021, DPDP Act 2023 and Rules 2025, Consumer Protection Act 2019
        and E-Commerce Rules 2020, Indian Contract Act 1872). They are a careful starting point, not legal advice — have them reviewed by a
        lawyer practising in India before relying on them.
      </Alert>
      {missing.length > 0 && (
        <Alert severity="error" sx={{ mb: 2 }}>
          The documents still show "[to be updated]" for: {missing.join(', ')}. Fill these in below (or in Company Details).
        </Alert>
      )}
      {msg && <Alert severity={msg.severity} sx={{ mb: 2 }} onClose={() => setMsg(null)}>{msg.text}</Alert>}

      <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" fontWeight={600} mb={2}>Grievance Officer & jurisdiction</Typography>
        {form && (
          <Grid container spacing={2}>
            {FIELDS.map(([k, label, help]) => (
              <Grid item xs={12} sm={6} key={k}>
                <TextField label={label} value={form[k]} onChange={e => setForm(f => ({ ...f, [k]: e.target.value }))} fullWidth size="small" helperText={help || ' '} />
              </Grid>
            ))}
          </Grid>
        )}
        <Button variant="contained" startIcon={<SaveIcon />} disabled={!form || loading} onClick={() => save({ variables: { input: form } })}>Save</Button>
      </Paper>

      <Paper variant="outlined" sx={{ overflowX: 'auto' }}>
        <Table size="small">
          <TableHead><TableRow>
            <TableCell>Document</TableCell><TableCell>Version</TableCell><TableCell>Effective</TableCell><TableCell>Applies to</TableCell><TableCell>Accepted by</TableCell><TableCell align="right" />
          </TableRow></TableHead>
          <TableBody>
            {docs.map(d => {
              const st = stats.find(x => x.key === d.key);
              return (
                <TableRow key={d.key}>
                  <TableCell>
                    <Typography variant="body2" fontWeight={600}>{d.title}</Typography>
                    {d.requiresAcceptance && <Chip size="small" color="primary" variant="outlined" label="Must be accepted" sx={{ mt: 0.5 }} />}
                  </TableCell>
                  <TableCell>{d.version}</TableCell>
                  <TableCell>{d.effectiveDate}</TableCell>
                  <TableCell>{{ all: 'Everyone', family: 'Families', vendor: 'Vendors', matchmaker: 'Matchmakers' }[d.audience] || d.audience}</TableCell>
                  <TableCell>{d.requiresAcceptance ? `${st?.accepted || 0} user(s)` : '—'}</TableCell>
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                    {SLUG[d.key] && <Button size="small" startIcon={<OpenInNewIcon />} href={`/legal/${SLUG[d.key]}`} target="_blank">View</Button>}
                    <Button size="small" startIcon={<EditIcon />} onClick={() => setEditing(d.key)}>Edit</Button>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Paper>

      {editing && (
        <EditDialog docKey={editing} onClose={() => setEditing(null)}
          onPublished={(text) => { setEditing(null); setMsg({ severity: 'success', text }); refetchDocs(); refetchStats(); }} />
      )}
    </Box>
  );
}
